import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';

import { Select as AntdSelect, Spin } from 'antd';
import type { SelectProps as AntdSelectProps, RefSelectProps } from 'antd';

const DEFAULT_LOADING_TEXT = '載入中...';
const DEFAULT_EMPTY_TEXT = '無可用選項';

type Primitive = string | number | boolean | null;

const isPrimitive = (value: unknown): value is Primitive => {
  const type = typeof value;
  return value === null || type === 'string' || type === 'number' || type === 'boolean';
};

interface SelectOption {
  value?: unknown;
  id?: unknown;
  key?: unknown;
  label?: unknown;
  [key: string]: unknown;
}

interface NormalizedOption {
  value: unknown;
  label: unknown;
  rawOption: SelectOption;
  [key: string]: unknown;
}

interface OptionsHookResult {
  options?: SelectOption[];
  loading?: boolean;
  error?: unknown;
  loadOptions?: () => unknown;
}

interface FetchDataParams {
  search?: string;
  sinceId?: unknown;
  pageSize?: number;
}

interface FetchDataResult {
  options?: SelectOption[];
  hasMore?: boolean;
  sinceId?: unknown;
}

interface SelectProps {
  // options shape varies per caller; 用 any[] 放行避免每個 caller 都得加 [key: string]: unknown
  options?: any[];
  value?: unknown;
  defaultValue?: unknown;
  // onChange 在 runtime 收到 normalized labelInValue 物件 ({ value, label, rawOption }) 或 null，
  // 但 caller 可能僅取 value 或自定 narrower type；用 any 放行避免型別膨脹
  onChange?: (value: any) => void;
  // antd-passthrough: 'loading' 屬於 antd Select 原生 prop，wrapper 雖內部用 isLoading，
  // 也允許 caller 直接傳 loading 給 antd（runtime 等價）
  loading?: boolean;
  onInputChange?: (input: string, meta: { action: string }) => void;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  onMenuScrollToBottom?: (event: unknown) => void;
  isClearable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  placeholder?: React.ReactNode;
  optionsHook?: () => OptionsHookResult | SelectOption[] | null | undefined;
  formatOptionLabel?: (option: SelectOption) => React.ReactNode;
  loadingMessage?: () => React.ReactNode;
  noOptionsMessage?: (meta: { inputValue: string }) => React.ReactNode;
  filterOption?: ((option: SelectOption, input: string) => boolean) | null;
  popupRender?: AntdSelectProps['popupRender'];
  className?: string;
  // 遠端資料獲取相關參數
  fetchData?: ((params: FetchDataParams) => Promise<FetchDataResult>) | null; // 資料獲取函數：({ search, sinceId, pageSize }) => Promise<{ options, hasMore, sinceId }>
  pageSize?: number;           // 每頁筆數
  searchDebounceMs?: number;   // 搜尋 debounce 毫秒數
}

// `...rest` 透傳到底層 AntdSelect，允許任意 antd Select props（mode、style、tagRender、size 等）。
// callers 仍以 `as React.ComponentType<any>` 包這個 Select，等同於 .jsx 邊界放行；本 Props 只標自訂行為。
type SelectComponentProps = SelectProps;

export const Select = React.forwardRef<RefSelectProps, SelectComponentProps>(({
  options = [],
  value,
  defaultValue,
  onChange,
  onInputChange,
  onMenuOpen,
  onMenuClose,
  onMenuScrollToBottom,
  isClearable = true,
  isDisabled = false,
  isLoading = false,
  isSearchable = true,
  placeholder,
  optionsHook,
  formatOptionLabel,
  loadingMessage = () => DEFAULT_LOADING_TEXT,
  noOptionsMessage = ({ inputValue }) => (inputValue ? `找不到 "${inputValue}" 相關選項` : DEFAULT_EMPTY_TEXT),
  filterOption,
  popupRender,
  className,
  // 遠端資料獲取相關參數
  fetchData = null,        // 資料獲取函數：({ search, sinceId, pageSize }) => Promise<{ options, hasMore, sinceId }>
  pageSize = 20,           // 每頁筆數
  searchDebounceMs = 800,  // 搜尋 debounce 毫秒數
  ...rest
}, ref) => {
  const [searchValue, setSearchValue] = useState('');

  // 遠端資料狀態
  const [remoteOptions, setRemoteOptions] = useState<SelectOption[]>([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteSinceId, setRemoteSinceId] = useState<unknown>(null);
  const [remoteHasMore, setRemoteHasMore] = useState(false);

  const hookLoadTriggeredRef = useRef(false);
  const searchDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPastingRef = useRef(false);
  const remoteLoadingRef = useRef(false);
  const remoteSinceIdRef = useRef<unknown>(null);

  // 遠端資料獲取函數
  const loadRemoteData = useCallback(async ({ search = '', reset = false }: { search?: string; reset?: boolean } = {}) => {
    console.log('[Select] loadRemoteData called:', { search, reset, loading: remoteLoadingRef.current, sinceId: remoteSinceIdRef.current });
    if (!fetchData || remoteLoadingRef.current) {
      console.log('[Select] loadRemoteData skipped:', { hasFetchData: !!fetchData, loading: remoteLoadingRef.current });
      return;
    }

    try {
      remoteLoadingRef.current = true;
      setRemoteLoading(true);
      const currentSinceId = reset ? null : remoteSinceIdRef.current;

      console.log('[Select] Fetching data:', { search, sinceId: currentSinceId, pageSize });
      const result = await fetchData({
        search,
        sinceId: currentSinceId,
        pageSize
      });
      console.log('[Select] Fetch result:', { optionsCount: result.options?.length, hasMore: result.hasMore, sinceId: result.sinceId });

      if (reset) {
        setRemoteOptions(result.options || []);
      } else {
        setRemoteOptions(prev => [...prev, ...(result.options || [])]);
      }

      setRemoteHasMore(result.hasMore || false);
      const newSinceId = result.sinceId;
      setRemoteSinceId(newSinceId);
      remoteSinceIdRef.current = newSinceId;
    } catch (error) {
      console.error('Select fetchData error:', error);
    } finally {
      remoteLoadingRef.current = false;
      setRemoteLoading(false);
    }
  }, [fetchData, pageSize]);

  // 當有 fetchData 時，下拉框打開時載入初始資料
  // 已移除自動載入，改為在 handleDropdownVisibleChange 中觸發
  // useEffect(() => {
  //   if (fetchData && remoteOptions.length === 0 && !remoteLoading) {
  //     // 延遲執行，避免在渲染時觸發
  //     const timer = setTimeout(() => {
  //       loadRemoteData({ reset: true });
  //     }, 0);
  //     return () => clearTimeout(timer);
  //   }
  // }, [fetchData, remoteOptions.length, remoteLoading, loadRemoteData]);

  // 保持 loadRemoteData ref 為最新版本，避免 setTimeout stale closure
  const loadRemoteDataRef = useRef(loadRemoteData);
  useEffect(() => {
    loadRemoteDataRef.current = loadRemoteData;
  });

  // 清理 debounce timer
  useEffect(() => {
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, []);

  const hookResult = optionsHook ? optionsHook() : null;

  const hookOptions = Array.isArray(hookResult)
    ? hookResult
    : (hookResult as OptionsHookResult | null)?.options;

  const hookLoading = typeof hookResult === 'object' && hookResult !== null && 'loading' in hookResult
    ? Boolean((hookResult as OptionsHookResult).loading)
    : false;

  const hookError = typeof hookResult === 'object' && hookResult !== null && 'error' in hookResult
    ? (hookResult as OptionsHookResult).error
    : null;

  const hookLoadOptions = typeof hookResult === 'object' && hookResult !== null && 'loadOptions' in hookResult
    ? (hookResult as OptionsHookResult).loadOptions
    : null;

  useEffect(() => {
    hookLoadTriggeredRef.current = false;
  }, [optionsHook, hookLoadOptions]);

  useEffect(() => {
    if (!optionsHook || !hookLoadOptions || hookLoadTriggeredRef.current) {
      return;
    }
    hookLoadTriggeredRef.current = true;

    try {
      const maybePromise = hookLoadOptions() as { finally?: (cb: () => void) => unknown } | undefined;
      if (maybePromise && typeof maybePromise.finally === 'function') {
        maybePromise.finally(() => {
          // no-op, ensure promise handled
        });
      }
    } catch (err) {
      hookLoadTriggeredRef.current = false;
    }
  }, [optionsHook, hookLoadOptions]);

  // 選擇 options 來源：遠端資料 > hook options > props options
  const sourceOptions = fetchData
    ? remoteOptions
    : (Array.isArray(hookOptions) ? hookOptions : options);

  const normalizedOptions = useMemo<NormalizedOption[]>(() => {
    const list = Array.isArray(sourceOptions) ? sourceOptions : [];

    return list.map((option: SelectOption, index: number) => {
      let normalizedValue: unknown;
      if (Object.prototype.hasOwnProperty.call(option, 'value')) {
        normalizedValue = option.value;
      } else if (Object.prototype.hasOwnProperty.call(option, 'id')) {
        normalizedValue = option.id;
      } else if (Object.prototype.hasOwnProperty.call(option, 'key')) {
        normalizedValue = option.key;
      }

      if (normalizedValue === undefined || normalizedValue === null) {
        normalizedValue = isPrimitive(option.label)
          ? option.label
          : `option_${index}`;
      }

      const computedLabel = formatOptionLabel
        ? formatOptionLabel(option)
        : option.label ?? (isPrimitive(option.value) ? String(option.value ?? '') : String(normalizedValue ?? ''));

      return {
        ...option,
        value: normalizedValue,
        label: computedLabel,
        rawOption: option,
      };
    });
  }, [sourceOptions, formatOptionLabel]);

  const optionMap = useMemo(() => {
    const map = new Map<unknown, NormalizedOption>();
    normalizedOptions.forEach((opt) => {
      map.set(opt.value, opt);
    });
    return map;
  }, [normalizedOptions]);

  const normalizeValue = useCallback((target: unknown): unknown => {
    if (target === undefined) return undefined;
    if (target === null) return null;
    if (Array.isArray(target)) {
      return target
        .map((item) => normalizeValue(item))
        .filter((item) => item !== undefined && item !== null);
    }
    if (isPrimitive(target)) {
      const match = optionMap.get(target);
      if (match) {
        return { value: match.value, label: match.label };
      }
      return { value: target, label: String(target) };
    }
    if (typeof target === 'object' && target !== null) {
      const targetObj = target as { value?: unknown; label?: unknown };
      const key = Object.prototype.hasOwnProperty.call(target, 'value') ? targetObj.value : undefined;
      if (key !== undefined) {
        const match = optionMap.get(key);
        if (match) {
          return { value: match.value, label: match.label };
        }
        const label = formatOptionLabel
          ? formatOptionLabel(target as SelectOption)
          : targetObj.label ?? (isPrimitive(key) ? String(key) : DEFAULT_EMPTY_TEXT);
        return { value: key, label };
      }
    }
    return undefined;
  }, [optionMap, formatOptionLabel]);

  const adaptedFilterOption = useMemo(() => {
    if (!isSearchable) return false;
    if (filterOption === null) return false;
    if (typeof filterOption === 'function') {
      return (input: string, option: { rawOption?: SelectOption } | undefined) =>
        filterOption((option?.rawOption ?? option) as SelectOption, input);
    }
    return undefined;
  }, [filterOption, isSearchable]);

  const handleChange = useCallback((selectedValue: unknown, option: unknown) => {
    if (!onChange) return;

    if (Array.isArray(option)) {
      const normalized = option.map((item: { rawOption?: SelectOption; value?: unknown; label?: unknown } | undefined) =>
        item?.rawOption ?? ({ value: item?.value, label: item?.label }));
      onChange(normalized);
    } else {
      const opt = option as { rawOption?: SelectOption } | undefined;
      const sel = selectedValue as { value?: unknown; label?: unknown } | null | undefined;
      const normalized = opt?.rawOption ?? (sel ? { value: sel.value, label: sel.label } : null);
      onChange(normalized);
    }
  }, [onChange]);

  const handleDropdownVisibleChange = useCallback((open: boolean) => {
    if (open) {
      onMenuOpen?.();
      // Lazy load: 如果有 fetchData 且目前沒有選項，則載入
      if (fetchData && remoteOptions.length === 0 && !remoteLoading) {
        loadRemoteData({ reset: true });
      }
    } else {
      onMenuClose?.();
    }
  }, [onMenuOpen, onMenuClose, fetchData, remoteOptions.length, remoteLoading, loadRemoteData]);

  const handlePopupScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    // 優先處理遠端資料的無限捲動
    if (fetchData && remoteHasMore && !remoteLoading) {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const { scrollTop, clientHeight, scrollHeight } = target;
      if (scrollHeight - (scrollTop + clientHeight) <= 8) {
        loadRemoteData({ search: searchValue });
        return;
      }
    }

    // 其他情況交給 onMenuScrollToBottom
    if (!onMenuScrollToBottom) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const { scrollTop, clientHeight, scrollHeight } = target;
    if (scrollHeight - (scrollTop + clientHeight) <= 8) {
      onMenuScrollToBottom(event);
    }
  }, [fetchData, remoteHasMore, remoteLoading, searchValue, loadRemoteData, onMenuScrollToBottom]);

  const combinedLoading = isLoading || hookLoading || remoteLoading;

  const resolvedNoOptionsMessage = () => {
    if (hookError) {
      if (typeof hookError === 'string') {
        return `載入失敗: ${hookError}`;
      }
      return '載入失敗';
    }
    return noOptionsMessage({ inputValue: searchValue });
  };

  const notFoundContent = combinedLoading
    ? (<div style={{ padding: '8px 12px', textAlign: 'center' }}><Spin size="small" /> <span style={{ marginLeft: 8 }}>{loadingMessage()}</span></div>)
    : (<div style={{ padding: '8px 12px', textAlign: 'center', color: '#8c8c8c' }}>{resolvedNoOptionsMessage()}</div>);

  // AntdSelect 的 props signature 與本 wrapper 自訂的 value/onChange/options/filterOption shape 不完全相容（labelInValue + rawOption），
  // 用 ComponentType<any> 在這個 boundary 放行，保留 runtime 行為。
  const AntdSelectAny = AntdSelect as unknown as React.ComponentType<any>;

  return (
    <AntdSelectAny
      ref={ref}
      className={className}
      labelInValue
      showSearch={isSearchable}
      allowClear={isClearable}
      disabled={isDisabled}
      loading={combinedLoading}
      placeholder={placeholder}
      options={normalizedOptions}
      value={normalizeValue(value)}
      defaultValue={normalizeValue(defaultValue)}
      filterOption={fetchData ? false : adaptedFilterOption}
      notFoundContent={notFoundContent}
      popupRender={popupRender}
      onOpenChange={handleDropdownVisibleChange}
      onPopupScroll={handlePopupScroll}
      onPaste={() => {
        isPastingRef.current = true;
      }}
      onSearch={(input: string) => {
        console.log('[Select] onSearch fired:', { input, hasFetchData: !!fetchData, searchDebounceMs });
        setSearchValue(input);

        // 如果有 fetchData，則以 search 參數傳送到後端
        if (fetchData) {
          // 清除之前的計時器
          if (searchDebounceTimerRef.current) {
            clearTimeout(searchDebounceTimerRef.current);
          }

          if (isPastingRef.current) {
            console.log('[Select] Paste detected, immediate search:', input);
            loadRemoteDataRef.current({ search: input, reset: true });
            isPastingRef.current = false;
          } else {
            console.log('[Select] Setting debounce timer:', searchDebounceMs, 'ms');
            searchDebounceTimerRef.current = setTimeout(() => {
              console.log('[Select] Debounce fired, calling loadRemoteData:', { search: input, remoteLoading: remoteLoadingRef.current });
              loadRemoteDataRef.current({ search: input, reset: true });
            }, searchDebounceMs);
          }
        }

        onInputChange?.(input, { action: 'input-change' });
      }}
      onChange={handleChange}
      {...rest}
    />
  );
});

Select.displayName = 'Select';

export default Select;
